/* eslint-disable no-console */
import React, { useState } from 'react';
import styles from '../styles/program.module.css';
import { createRazorPayOrder, verifyPayment } from '../client';

interface PriceCalculatorProps {
  basePrice: number;
  programTitle: string;
}
export interface UserPaymentInfo {
  fullName: string;
  email: string;
  whatsappNumber: string;
}

export interface PaymentFormErrors {
  fullName?: string;
  email?: string;
  whatsappNumber?: string;
}
const defaultUserValues = {
  fullName: '',
  email: '',
  whatsappNumber: '',
};
const PriceCalculator: React.FC<PriceCalculatorProps> = ({ basePrice, programTitle }) => {
  const [promoCode, setPromoCode] = useState('');
  const [discount, setDiscount] = useState(0);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showUserForm, setShowUserForm] = useState(false);
  const [userInfo, setUserInfo] = useState<UserPaymentInfo>(defaultUserValues);
  const [formErrors, setFormErrors] = useState<PaymentFormErrors>({});

  const gstRate = 0.18;
  const gstAmount = basePrice * gstRate;
  const subtotal = basePrice + gstAmount;
  const total = (subtotal - discount).toFixed();

  const validateForm = (): boolean => {
    const errors: PaymentFormErrors = {};

    if (!userInfo.fullName.trim()) {
      errors.fullName = 'Full name is required';
    }

    if (!userInfo.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(userInfo.email)) {
      errors.email = 'Invalid email format';
    }

    if (!userInfo.whatsappNumber.trim()) {
      errors.whatsappNumber = 'WhatsApp number is required';
    } else if (!/^\d{10}$/.test(userInfo.whatsappNumber)) {
      errors.whatsappNumber = 'Invalid WhatsApp number (10 numbers required)';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handlePromoCode = () => {
    if (promoCode === 'DZN84R') {
      setDiscount(100);
      setError('');
    } else if (promoCode === 'TQJ56M') {
      setDiscount(100);
      setError('');
    } else if (promoCode === 'FIRST100') {
      setDiscount(100);
      setError('');
    } else {
      setDiscount(0);
      setError('Invalid promo code');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setUserInfo(prev => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (formErrors[name as keyof PaymentFormErrors]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handlePayment = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      const orderResponse = await createRazorPayOrder({
        fullName: userInfo.fullName,
        email: userInfo.email,
        whatsappNumber: parseInt(userInfo.whatsappNumber),
        amount: parseInt(total),
        program: programTitle,
        referredBy: promoCode,
      });

      const orderData = orderResponse.data;

      if (!orderData.orderId) {
        throw new Error('Failed to create order');
      }

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: parseInt(total) * 100,
        currency: 'INR',
        name: 'Tech Career Hub',
        description: `Enrollment for ${programTitle}`,
        order_id: orderData.orderId,
        prefill: {
          name: userInfo.fullName,
          email: userInfo.email,
          contact: userInfo.whatsappNumber,
        },
        handler: async function (response: any) {
          try {
            const verifyResponse = await verifyPayment({
              razorpayOrderId: response.razorpay_order_id,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpaySignature: response.razorpay_signature,
              enrollmentId: orderData.enrollMentId,
            });

            if (verifyResponse.data.success) {
              alert('Payment successful! You are now enrolled in the program.');
              setShowUserForm(false);
            }
          } catch (error) {
            console.log('Payment verification failed:', error);
            alert('Payment verification failed. Please contact support.');
          }
        },
        theme: {
          color: '#1a1a1a',
        },
      };

      const razorpayInstance = new (window as any).Razorpay(options);
      razorpayInstance.open();
    } catch (error) {
      console.error('Payment initialization failed:', error);
      alert('Failed to initialize payment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.priceBreakdown}>
      <h3 className={styles.infoTitle}>Price Breakdown</h3>
      <div className={styles.priceItem}>
        <span>Base Price</span>
        <span>₹{basePrice}</span>
      </div>
      <div className={styles.priceItem}>
        <span>GST (18%)</span>
        <span>₹{gstAmount}</span>
      </div>
      {discount > 0 && (
        <div className={styles.priceItem}>
          <span>Discount</span>
          <span>-₹{discount}</span>
        </div>
      )}
      <div className={styles.promoCodeSection}>
        <input
          type="text"
          placeholder="Enter promo code"
          value={promoCode}
          onChange={e => setPromoCode(e.target.value.toUpperCase())}
          className={styles.promoInput}
        />
        <button onClick={handlePromoCode} className={styles.promoButton}>
          Apply
        </button>
      </div>
      {error && <p className={styles.errorText}>{error}</p>}
      <div className={styles.totalPrice}>
        <span>Total Amount</span>
        <span>₹{total}</span>
      </div>

      {!showUserForm ? (
        <button onClick={() => setShowUserForm(true)} className={styles.paymentButton}>
          Proceed to Enrollment
        </button>
      ) : (
        <div className={styles.userForm}>
          <h4 className={styles.formTitle}>Enter Your Details</h4>
          <div className={styles.formGroup}>
            <input
              type="text"
              name="fullName"
              value={userInfo.fullName}
              onChange={handleInputChange}
              placeholder="Full Name"
              className={styles.formInput}
            />
            {formErrors.fullName && <span className={styles.errorText}>{formErrors.fullName}</span>}
          </div>
          <div className={styles.formGroup}>
            <input
              type="email"
              name="email"
              value={userInfo.email}
              onChange={handleInputChange}
              placeholder="Email Address"
              className={styles.formInput}
            />
            {formErrors.email && <span className={styles.errorText}>{formErrors.email}</span>}
          </div>
          <div className={styles.formGroup}>
            <input
              type="tel"
              name="whatsappNumber"
              value={userInfo.whatsappNumber}
              onChange={handleInputChange}
              placeholder="WhatsApp Number"
              className={styles.formInput}
            />
            {formErrors.whatsappNumber && (
              <span className={styles.errorText}>{formErrors.whatsappNumber}</span>
            )}
          </div>
          <button onClick={handlePayment} className={styles.paymentButton} disabled={loading}>
            {loading ? 'Processing...' : 'Proceed to Payment'}
          </button>
        </div>
      )}
    </div>
  );
};

export default PriceCalculator;
